/**
 * Safe SMRS invoice regeneration rules (KAN-284).
 * Generate only refreshes company-periods whose content came from device reports; anything created or
 * edited on the SMRS invoice page (manual, import, adjustment, Fee Collection transfer rows) is kept.
 */
import { SmrsRate } from './fee-rate.service';

export type GenerationClass = 'new' | 'refresh' | 'kept-manual' | 'kept-canceled';

export interface GeneratedTotals {
  totalRevenue: number;
  totalFee: number;
  totalLfcFee: number;
  totalKcegFee: number;
}

export interface ForwardInvoice {
  period: string;
  status?: string;
  totalFee?: number;
  amountPaid?: number;
  remainingBalance?: number;
  beginningBalanceOverride?: number;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** A row written by Generate: legacy `generated_` import id, or tagged `source: 'generated'`. */
export function isGeneratedAggregation(agg: any): boolean {
  if (!agg || agg.isManual === true || agg.isAdjustment === true) return false;
  return agg.source === 'generated' || String(agg.importId || '').startsWith('generated_');
}

/**
 * Same ownership rule as `recalculateLedgerForward`: tagged rows by `companyId`, legacy untagged rows by store.
 * Tagged generated rows of another brand (hybrid store) never belong to this run.
 */
export function aggregationBelongsToCompany(agg: any, companyId: string, companyStoreIds: Set<string>, brandId?: string): boolean {
  if (brandId && agg?.brandId && agg.brandId !== brandId) return false;
  if (agg?.companyId) return agg.companyId === companyId;
  return companyStoreIds.has(String(agg?.storeId || '').trim());
}

export function classifyCompanyPeriod(invoice: any | null, companyAggs: any[]): GenerationClass {
  if (invoice?.status === 'Canceled') return 'kept-canceled';
  if (invoice?.isManual === true) return 'kept-manual';
  if (companyAggs.some(a => !isGeneratedAggregation(a))) return 'kept-manual';
  return invoice ? 'refresh' : 'new';
}

function revenueForRate(revenues: Record<string, number>, rate: SmrsRate): number {
  let val = 0;
  rate.importKeys?.forEach(k => { if (revenues[k]) val += Number(revenues[k]) || 0; });
  if (rate.id && revenues[rate.id]) val += Number(revenues[rate.id]) || 0;
  return val;
}

/** Revenue and fee split for the new generated rows, using the `recalculateLedgerForward` formula. */
/** A New or Refresh company that will actually change. Only these are listed, re-planned and written. */
export function willGenerate(c: { classification: GenerationClass, unchanged: boolean }): boolean {
  return (c.classification === 'new' || c.classification === 'refresh') && !c.unchanged;
}

export function computeGeneratedTotals(storeItems: { revenues: Record<string, number> }[], rates: SmrsRate[]): GeneratedTotals {
  let totalRevenue = 0, totalFee = 0, totalLfcFee = 0, totalKcegFee = 0;
  for (const item of storeItems) {
    for (const r of rates) {
      const val = revenueForRate(item.revenues || {}, r);
      const fee = val * ((r.rate || 0) / 100);
      totalRevenue += val;
      totalFee += fee;
      if ((r.rate || 0) !== 0) {
        const kcegRate = (r as any).kcegRate !== undefined && (r as any).kcegRate !== null ? Number((r as any).kcegRate) : 0;
        totalKcegFee += fee * (kcegRate / r.rate);
        totalLfcFee += fee * (((r.rate || 0) - kcegRate) / r.rate);
      }
    }
  }
  return {
    totalRevenue: round2(totalRevenue),
    totalFee: round2(totalFee),
    totalLfcFee: round2(totalLfcFee),
    totalKcegFee: round2(totalKcegFee)
  };
}

export function deriveStatus(remainingBalance: number, amountPaid: number): string {
  if (remainingBalance <= 0.01) return 'Paid';
  if (amountPaid > 0.01) return 'Partial';
  return 'Not Paid';
}

/**
 * Forward balance impact of a new remaining balance, mirroring `recalculateLedgerForward`:
 * later fees and payments are kept, a beginning-balance override restarts the chain, a canceled invoice resets it.
 */
export function simulateForwardBalances(startRemaining: number, laterInvoices: ForwardInvoice[]): { affectedPeriods: string[], latestRemainingChange: number } {
  const sorted = [...laterInvoices].sort((a, b) => a.period.localeCompare(b.period));
  const affectedPeriods: string[] = [];
  let carry = startRemaining;
  let latestRemainingChange = 0;
  for (const inv of sorted) {
    if (inv.status === 'Canceled') { carry = 0; continue; }
    const beginning = inv.beginningBalanceOverride !== undefined ? Number(inv.beginningBalanceOverride) : carry;
    const remaining = round2(beginning + Number(inv.totalFee || 0) - Number(inv.amountPaid || 0));
    const change = round2(remaining - Number(inv.remainingBalance || 0));
    if (Math.abs(change) > 0.001) affectedPeriods.push(inv.period);
    latestRemainingChange = change;
    carry = remaining;
  }
  return { affectedPeriods, latestRemainingChange };
}

/**
 * True when the company's current generated rows (already filtered to this company and brand) hold exactly these
 * store revenues, one row per store. Legacy untagged rows count too, so a rerun that would only retag is a no-op.
 */
export function generatedRowsMatch(existing: any[], storeItems: { storeId: string, revenues: Record<string, number> }[]): boolean {
  if (existing.length !== storeItems.length) return false;
  const byStore = new Map<string, any>();
  for (const agg of existing) {
    const sid = String(agg.storeId || '').trim();
    if (byStore.has(sid)) return false;
    byStore.set(sid, agg);
  }
  return storeItems.every(item => {
    const agg = byStore.get(item.storeId);
    if (!agg) return false;
    const a = agg.revenues || {};
    const b = item.revenues || {};
    const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
    return [...keys].every(k => Math.abs((Number(a[k]) || 0) - (Number(b[k]) || 0)) < 0.005);
  });
}
